import React, { useMemo } from 'react';
import { TableOfContentV2Styles as classes } from './TableOfContentsV2Styles';
import { useTranslation } from '@stateManagement/TranslationContext';
import { getTranslation } from '@helpers/languageTranslations';
import { getHeadingsFromContent } from './helperTableOfContents';
import { Heading, Container, HeadingVariant } from '@web-for-marketing/react-ui';
import { useTableOfContents } from '@hooks/useTableOfContents';
import { TableOfContentsItems } from './TableOfContentsItems';
import { useAtom } from 'jotai';
import { navHeightAtom } from '@atoms/appSettings';

interface TableOfContentsProps {
    content: string;
    id?: string;
    headingLevel?: HeadingVariant;
}

export const TableOfContents = ({
    content,
    id,
    headingLevel = 'h3',
}: TableOfContentsProps): React.ReactElement | null => {
    const { nestedHeadingsH2, nestedHeadingsH3 } = useMemo(() => getHeadingsFromContent(content), [content]);
    const { selectedLanguageTranslation } = useTranslation();
    const [navHeight] = useAtom(navHeightAtom);
    const tableOfContentTranslation = useMemo(() => {
        return getTranslation(selectedLanguageTranslation, 'blogTableContent');
    }, [selectedLanguageTranslation]);
    const { activeHeader } = useTableOfContents(content);

    const removeFocusAfterScroll = (): void => {
        document.addEventListener('scroll', () => {
            (document?.activeElement as HTMLElement)?.blur();
        });
    };

    const scrollToElement = (id: string, event: React.MouseEvent<HTMLElement>): void => {
        event.preventDefault();
        const element = document.querySelector(`#${id}`);
        if (element) {
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - navHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        }
    };

    function handleItemSelect(id: string, event: React.MouseEvent<HTMLElement>): void {
        scrollToElement(id, event);
        removeFocusAfterScroll();
    }

    if (nestedHeadingsH2.length > 0 || nestedHeadingsH3.length > 0) {
        return (
            <Container maxWidth={450} id={id} css={classes.containerToC}>
                <Heading variant='h4' component={headingLevel} color='activePrimary' weight='bold' css={classes.title}>
                    {tableOfContentTranslation.title}
                </Heading>
                <ul css={classes.listUl}>
                    <TableOfContentsItems
                        items={nestedHeadingsH2.length > 0 ? nestedHeadingsH2 : nestedHeadingsH3}
                        activeHeader={activeHeader}
                        onSelectItem={handleItemSelect}
                    />
                </ul>
            </Container>
        );
    } else {
        return null;
    }
};
